import { computed, inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  deleteDoc,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AuthStore } from '../auth/auth.store';
import {
  PayrollSettings,
  DeductionTypeDefinition,
  BenefitTypeDefinition,
  EmployeePayrollProfile,
  PayrollRun,
  Payslip,
  CalculatePayrollRunRequest,
  CalculatePayrollRunResponse,
  ApprovePayrollRunRequest,
  ApprovePayrollRunResponse,
  MarkPayrollPaidRequest,
  MarkPayrollPaidResponse,
  VoidPayslipRequest,
  VoidPayslipResponse,
  ExportPayrollRunRequest,
  ExportPayrollRunResponse,
} from '../models/payroll.model';

@Injectable({ providedIn: 'root' })
export class PayrollService {
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);

  // Private signals
  private readonly _settings = signal<PayrollSettings | null>(null);
  private readonly _deductionTypes = signal<DeductionTypeDefinition[]>([]);
  private readonly _benefitTypes = signal<BenefitTypeDefinition[]>([]);
  private readonly _runs = signal<PayrollRun[]>([]);
  private readonly _currentRun = signal<PayrollRun | null>(null);
  private readonly _payslips = signal<Payslip[]>([]);
  private readonly _myPayslips = signal<Payslip[]>([]);
  private readonly _employeeProfiles = signal<EmployeePayrollProfile[]>([]);
  private readonly _loading = signal(false);

  // Public readonly signals
  readonly settings = this._settings.asReadonly();
  readonly deductionTypes = this._deductionTypes.asReadonly();
  readonly benefitTypes = this._benefitTypes.asReadonly();
  readonly runs = this._runs.asReadonly();
  readonly currentRun = this._currentRun.asReadonly();
  readonly payslips = this._payslips.asReadonly();
  readonly myPayslips = this._myPayslips.asReadonly();
  readonly employeeProfiles = this._employeeProfiles.asReadonly();
  readonly loading = this._loading.asReadonly();

  // Computed signals
  readonly activeDeductionTypes = computed(() =>
    this._deductionTypes().filter((d) => d.isActive)
  );

  readonly activeBenefitTypes = computed(() =>
    this._benefitTypes().filter((b) => b.isActive)
  );

  readonly pendingRuns = computed(() =>
    this._runs().filter((r) => r.status === 'pending_approval')
  );

  readonly calculatedRuns = computed(() =>
    this._runs().filter((r) => r.status === 'calculated')
  );

  readonly approvedRuns = computed(() =>
    this._runs().filter((r) => r.status === 'approved')
  );

  readonly paidRuns = computed(() =>
    this._runs().filter((r) => r.status === 'paid')
  );

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) throw new Error('No user logged in');
    return id;
  }

  // ==================== Settings ====================

  async loadSettings(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/payrollSettings`);
      const snap = await getDocs(ref);

      if (snap.empty) {
        this._settings.set(null);
      } else {
        const docData = snap.docs[0];
        this._settings.set(this.mapSettings(docData.id, docData.data()));
      }
    } finally {
      this._loading.set(false);
    }
  }

  async updateSettings(settings: Partial<PayrollSettings>): Promise<void> {
    const currentSettings = this._settings();
    const settingsId = currentSettings?.id ?? 'default';
    const ref = doc(this.firestore, `tenants/${this.tenantId}/payrollSettings/${settingsId}`);

    const updateData: Record<string, unknown> = {
      ...settings,
      updatedAt: serverTimestamp(),
    };

    if (!currentSettings) {
      updateData['createdAt'] = serverTimestamp();
    }

    await setDoc(ref, updateData, { merge: true });
    await this.loadSettings();
  }

  // ==================== Deduction Types ====================

  async loadDeductionTypes(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/deductionTypes`);
      const q = query(ref, orderBy('name'));
      const snap = await getDocs(q);
      this._deductionTypes.set(snap.docs.map((d) => this.mapDeductionType(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async createDeductionType(
    deduction: Omit<DeductionTypeDefinition, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const ref = doc(this.firestore, `tenants/${this.tenantId}/deductionTypes/${id}`);

    await setDoc(ref, {
      ...deduction,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await this.loadDeductionTypes();
    return id;
  }

  async updateDeductionType(
    id: string,
    updates: Partial<DeductionTypeDefinition>
  ): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/deductionTypes/${id}`);
    await updateDoc(ref, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    await this.loadDeductionTypes();
  }

  async deleteDeductionType(id: string): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/deductionTypes/${id}`);
    await deleteDoc(ref);
    await this.loadDeductionTypes();
  }

  // ==================== Benefit Types ====================

  async loadBenefitTypes(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/benefitTypes`);
      const q = query(ref, orderBy('name'));
      const snap = await getDocs(q);
      this._benefitTypes.set(snap.docs.map((d) => this.mapBenefitType(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async createBenefitType(
    benefit: Omit<BenefitTypeDefinition, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const ref = doc(this.firestore, `tenants/${this.tenantId}/benefitTypes/${id}`);

    await setDoc(ref, {
      ...benefit,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await this.loadBenefitTypes();
    return id;
  }

  async updateBenefitType(
    id: string,
    updates: Partial<BenefitTypeDefinition>
  ): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/benefitTypes/${id}`);
    await updateDoc(ref, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    await this.loadBenefitTypes();
  }

  async deleteBenefitType(id: string): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/benefitTypes/${id}`);
    await deleteDoc(ref);
    await this.loadBenefitTypes();
  }

  // ==================== Employee Payroll Profiles ====================

  async loadEmployeeProfiles(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/employeePayrollProfiles`);
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      this._employeeProfiles.set(snap.docs.map((d) => this.mapEmployeeProfile(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async getEmployeeProfile(employeeId: string): Promise<EmployeePayrollProfile | null> {
    const ref = collection(this.firestore, `tenants/${this.tenantId}/employeePayrollProfiles`);
    const q = query(ref, where('employeeId', '==', employeeId));
    const snap = await getDocs(q);

    if (snap.empty) return null;
    return this.mapEmployeeProfile(snap.docs[0].id, snap.docs[0].data());
  }

  async createEmployeeProfile(
    profile: Omit<EmployeePayrollProfile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const ref = doc(this.firestore, `tenants/${this.tenantId}/employeePayrollProfiles/${id}`);

    await setDoc(ref, {
      ...profile,
      effectiveDate: profile.effectiveDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await this.loadEmployeeProfiles();
    return id;
  }

  async updateEmployeeProfile(
    id: string,
    updates: Partial<EmployeePayrollProfile>
  ): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/employeePayrollProfiles/${id}`);
    await updateDoc(ref, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    await this.loadEmployeeProfiles();
  }

  // ==================== Payroll Runs ====================

  async loadRuns(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/payrollRuns`);
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      this._runs.set(snap.docs.map((d) => this.mapRun(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async loadRunById(runId: string): Promise<PayrollRun | null> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/payrollRuns/${runId}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const run = this.mapRun(snap.id, snap.data());
    this._currentRun.set(run);
    return run;
  }

  async previewRun(request: Omit<CalculatePayrollRunRequest, 'isDryRun'>): Promise<CalculatePayrollRunResponse> {
    const calculateFn = httpsCallable<CalculatePayrollRunRequest, CalculatePayrollRunResponse>(
      this.functions,
      'calculatePayrollRun'
    );

    const result = await calculateFn({
      ...request,
      isDryRun: true,
    });

    return result.data;
  }

  async calculateRun(request: Omit<CalculatePayrollRunRequest, 'isDryRun'>): Promise<CalculatePayrollRunResponse> {
    const calculateFn = httpsCallable<CalculatePayrollRunRequest, CalculatePayrollRunResponse>(
      this.functions,
      'calculatePayrollRun'
    );

    const result = await calculateFn({
      ...request,
      isDryRun: false,
    });

    if (result.data.success) {
      await this.loadRuns();
    }

    return result.data;
  }

  async approveRun(runId: string, comments?: string): Promise<ApprovePayrollRunResponse> {
    const approveFn = httpsCallable<ApprovePayrollRunRequest, ApprovePayrollRunResponse>(
      this.functions,
      'approvePayrollRun'
    );

    const result = await approveFn({ runId, comments });

    if (result.data.success) {
      await this.loadRuns();
      await this.loadRunById(runId);
    }

    return result.data;
  }

  async markPaid(
    runId: string,
    generatePdfs: boolean = false,
    notifyEmployees: boolean = true
  ): Promise<MarkPayrollPaidResponse> {
    const markPaidFn = httpsCallable<MarkPayrollPaidRequest, MarkPayrollPaidResponse>(
      this.functions,
      'markPayrollPaid'
    );

    const result = await markPaidFn({ runId, generatePdfs, notifyEmployees });

    if (result.data.success) {
      await this.loadRuns();
      await this.loadRunById(runId);
    }

    return result.data;
  }

  async exportRun(
    runId: string,
    format: 'csv' | 'pdf',
    includeDetails: boolean = true
  ): Promise<ExportPayrollRunResponse> {
    const exportFn = httpsCallable<ExportPayrollRunRequest, ExportPayrollRunResponse>(
      this.functions,
      'exportPayrollRun'
    );

    const result = await exportFn({ runId, format, includeDetails });
    return result.data;
  }

  // ==================== Payslips ====================

  async loadPayslipsForRun(runId: string): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/payslips`);
      const q = query(ref, where('payrollRunId', '==', runId), orderBy('employeeName'));
      const snap = await getDocs(q);
      this._payslips.set(snap.docs.map((d) => this.mapPayslip(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async loadMyPayslips(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/payslips`);
      const q = query(
        ref,
        where('userId', '==', this.userId),
        where('status', '!=', 'voided'),
        orderBy('status'),
        orderBy('payDate', 'desc')
      );
      const snap = await getDocs(q);
      this._myPayslips.set(snap.docs.map((d) => this.mapPayslip(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async getPayslipById(payslipId: string): Promise<Payslip | null> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/payslips/${payslipId}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;
    return this.mapPayslip(snap.id, snap.data());
  }

  async voidPayslip(payslipId: string, reason: string): Promise<VoidPayslipResponse> {
    const voidFn = httpsCallable<VoidPayslipRequest, VoidPayslipResponse>(
      this.functions,
      'voidPayslip'
    );

    const result = await voidFn({ payslipId, reason });

    if (result.data.success) {
      // Refresh payslips if we have a current run loaded
      const currentRun = this._currentRun();
      if (currentRun) {
        await this.loadPayslipsForRun(currentRun.id);
      }
    }

    return result.data;
  }

  // ==================== Mappers ====================

  private mapSettings(id: string, data: Record<string, unknown>): PayrollSettings {
    return {
      id,
      payPeriodType: (data['payPeriodType'] as PayrollSettings['payPeriodType']) ?? 'monthly',
      payDayOfWeek: data['payDayOfWeek'] as number | undefined,
      payDayOfMonth: (data['payDayOfMonth'] as number) ?? 1,
      secondPayDayOfMonth: data['secondPayDayOfMonth'] as number | undefined,
      defaultCurrency: (data['defaultCurrency'] as string) ?? 'USD',
      overtimeMultiplier: (data['overtimeMultiplier'] as number) ?? 1.5,
      doubleOvertimeMultiplier: data['doubleOvertimeMultiplier'] as number | undefined,
      standardWorkHoursPerDay: (data['standardWorkHoursPerDay'] as number) ?? 8,
      standardWorkDaysPerWeek: (data['standardWorkDaysPerWeek'] as number) ?? 5,
      autoRunEnabled: (data['autoRunEnabled'] as boolean) ?? false,
      autoRunDaysBefore: (data['autoRunDaysBefore'] as number) ?? 3,
      requireApproval: (data['requireApproval'] as boolean) ?? true,
      approverRoles: (data['approverRoles'] as string[]) ?? ['hr_admin', 'finance_admin'],
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapDeductionType(id: string, data: Record<string, unknown>): DeductionTypeDefinition {
    return {
      id,
      name: (data['name'] as string) ?? '',
      code: (data['code'] as string) ?? '',
      description: data['description'] as string | undefined,
      calculationType: (data['calculationType'] as DeductionTypeDefinition['calculationType']) ?? 'fixed',
      amount: data['amount'] as number | undefined,
      percentage: data['percentage'] as number | undefined,
      tiers: data['tiers'] as DeductionTypeDefinition['tiers'],
      isPreTax: (data['isPreTax'] as boolean) ?? false,
      isMandatory: (data['isMandatory'] as boolean) ?? false,
      isActive: (data['isActive'] as boolean) ?? true,
      appliesToContractTypes: data['appliesToContractTypes'] as string[] | undefined,
      maxAnnualAmount: data['maxAnnualAmount'] as number | undefined,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapBenefitType(id: string, data: Record<string, unknown>): BenefitTypeDefinition {
    return {
      id,
      name: (data['name'] as string) ?? '',
      code: (data['code'] as string) ?? '',
      description: data['description'] as string | undefined,
      calculationType: (data['calculationType'] as BenefitTypeDefinition['calculationType']) ?? 'fixed',
      amount: data['amount'] as number | undefined,
      percentage: data['percentage'] as number | undefined,
      isTaxable: (data['isTaxable'] as boolean) ?? true,
      isActive: (data['isActive'] as boolean) ?? true,
      appliesToContractTypes: data['appliesToContractTypes'] as string[] | undefined,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapEmployeeProfile(id: string, data: Record<string, unknown>): EmployeePayrollProfile {
    return {
      id,
      employeeId: (data['employeeId'] as string) ?? '',
      userId: (data['userId'] as string) ?? '',
      baseSalary: (data['baseSalary'] as number) ?? 0,
      salaryType: (data['salaryType'] as EmployeePayrollProfile['salaryType']) ?? 'monthly',
      currency: (data['currency'] as string) ?? 'USD',
      bankAccountName: data['bankAccountName'] as string | undefined,
      bankAccountNumber: data['bankAccountNumber'] as string | undefined,
      bankName: data['bankName'] as string | undefined,
      bankRoutingNumber: data['bankRoutingNumber'] as string | undefined,
      taxId: data['taxId'] as string | undefined,
      taxFilingStatus: data['taxFilingStatus'] as string | undefined,
      allowances: data['allowances'] as number | undefined,
      deductionOverrides: (data['deductionOverrides'] as EmployeePayrollProfile['deductionOverrides']) ?? [],
      benefitOverrides: (data['benefitOverrides'] as EmployeePayrollProfile['benefitOverrides']) ?? [],
      isOnHold: (data['isOnHold'] as boolean) ?? false,
      holdReason: data['holdReason'] as string | undefined,
      effectiveDate: (data['effectiveDate'] as Timestamp)?.toDate() ?? new Date(),
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapRun(id: string, data: Record<string, unknown>): PayrollRun {
    return {
      id,
      periodStart: (data['periodStart'] as Timestamp)?.toDate() ?? new Date(),
      periodEnd: (data['periodEnd'] as Timestamp)?.toDate() ?? new Date(),
      payDate: (data['payDate'] as Timestamp)?.toDate() ?? new Date(),
      status: (data['status'] as PayrollRun['status']) ?? 'draft',
      summary: (data['summary'] as PayrollRun['summary']) ?? {
        totalEmployees: 0,
        totalGrossPay: 0,
        totalNetPay: 0,
        totalDeductions: 0,
        totalBenefits: 0,
        totalTaxes: 0,
      },
      approvals: (data['approvals'] as PayrollRun['approvals']) ?? [],
      calculatedAt: (data['calculatedAt'] as Timestamp)?.toDate(),
      calculatedBy: data['calculatedBy'] as string | undefined,
      approvedAt: (data['approvedAt'] as Timestamp)?.toDate(),
      approvedBy: data['approvedBy'] as string | undefined,
      paidAt: (data['paidAt'] as Timestamp)?.toDate(),
      paidBy: data['paidBy'] as string | undefined,
      voidedAt: (data['voidedAt'] as Timestamp)?.toDate(),
      voidedBy: data['voidedBy'] as string | undefined,
      voidReason: data['voidReason'] as string | undefined,
      notes: data['notes'] as string | undefined,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private mapPayslip(id: string, data: Record<string, unknown>): Payslip {
    return {
      id,
      payrollRunId: (data['payrollRunId'] as string) ?? '',
      employeeId: (data['employeeId'] as string) ?? '',
      userId: (data['userId'] as string) ?? '',
      employeeName: (data['employeeName'] as string) ?? '',
      employeeEmail: (data['employeeEmail'] as string) ?? '',
      periodStart: (data['periodStart'] as Timestamp)?.toDate() ?? new Date(),
      periodEnd: (data['periodEnd'] as Timestamp)?.toDate() ?? new Date(),
      payDate: (data['payDate'] as Timestamp)?.toDate() ?? new Date(),
      status: (data['status'] as Payslip['status']) ?? 'draft',
      earnings: (data['earnings'] as Payslip['earnings']) ?? [],
      deductions: (data['deductions'] as Payslip['deductions']) ?? [],
      benefits: (data['benefits'] as Payslip['benefits']) ?? [],
      grossPay: (data['grossPay'] as number) ?? 0,
      totalDeductions: (data['totalDeductions'] as number) ?? 0,
      totalBenefits: (data['totalBenefits'] as number) ?? 0,
      netPay: (data['netPay'] as number) ?? 0,
      ytdTotals: (data['ytdTotals'] as Payslip['ytdTotals']) ?? {
        grossPay: 0,
        netPay: 0,
        totalDeductions: 0,
        totalBenefits: 0,
        totalTaxes: 0,
      },
      hoursWorked: data['hoursWorked'] as number | undefined,
      overtimeHours: data['overtimeHours'] as number | undefined,
      pdfUrl: data['pdfUrl'] as string | undefined,
      issuedAt: (data['issuedAt'] as Timestamp)?.toDate(),
      voidedAt: (data['voidedAt'] as Timestamp)?.toDate(),
      voidedBy: data['voidedBy'] as string | undefined,
      voidReason: data['voidReason'] as string | undefined,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }
}
